import { useState } from "react";
import { Button } from "@/components/ui/button";
import { KeyRound, Copy, Check, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ResetPasswordButtonProps {
  userId: string;
  userName: string;
}

export const ResetPasswordButton = ({
  userId,
  userName,
}: ResetPasswordButtonProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [copied, setCopied] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "admin-reset-user-password",
        { body: { userId } },
      );

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Error desconocido");

      setGeneratedPassword(data.password);
      setConfirmOpen(false);
      setResultOpen(true);
    } catch (e: any) {
      console.error("Reset password error:", e);
      toast({
        variant: "destructive",
        title: "Error",
        description: e.message || "No se pudo generar la contraseña",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="sm:px-[15px] sm:w-auto"
            title="Restablecer contraseña"
          >
            <KeyRound className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-black/70 backdrop-blur-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Restablecer contraseña</AlertDialogTitle>
            <AlertDialogDescription>
              Se generará una nueva contraseña para{" "}
              <span className="font-semibold">{userName}</span>. Tendrás que
              dictársela. Cuando inicie sesión, deberá crear una nueva
              contraseña antes de poder usar la app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generando...
                </>
              ) : (
                "Generar contraseña"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent className="bg-black/70 backdrop-blur-md">
          <DialogHeader>
            <DialogTitle>Nueva contraseña generada</DialogTitle>
            <DialogDescription>
              Dictále esta contraseña a{" "}
              <span className="font-semibold">{userName}</span>. Cuando inicie
              sesión deberá crear una nueva. Esta contraseña solo se mostrará
              una vez.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 p-6 rounded-xl border-2 border-primary/40 bg-primary/5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
              Contraseña temporal
            </p>
            <p className="font-mono text-3xl font-bold tracking-widest text-center break-all">
              {generatedPassword}
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={handleCopy}
              className="flex-1"
            >
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Copiada
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copiar
                </>
              )}
            </Button>
            <Button onClick={() => setResultOpen(false)} className="flex-1">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
